import React, { useEffect, useState, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Container,
  Card,
  CardContent,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Requester from "../../../utils/requester";
import { useNotification } from '../../../context/NotificationContext';
import { useTranslation } from 'react-i18next';

export const timersLoader = async () => {
  try {
    const response = await Requester.get('scheduled-actions/list/');
    if (response.ok) {
      return await response.json();
    }
    return [];
  } catch (error) {
    console.error('Error loading timers:', error);
    return [];
  }
};

export default function TimerList() {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [timers, setTimers] = useState([]);
  const [updatingTimerIds, setUpdatingTimerIds] = useState([]);
  const [deletingTimerIds, setDeletingTimerIds] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTimer, setSelectedTimer] = useState(null);
  const initialRenderComplete = useRef(false);

  // Fetch timers data on initial mount
  useEffect(() => {
    if (initialRenderComplete.current) return;
    
    const fetchTimers = async () => {
      setLoading(true);
      try {
        const response = await Requester.get('scheduled-actions/list/');
        if (response.ok) {
          const data = await response.json();
          setTimers(data);
        } else {
          console.error('Failed to fetch timers');
          showNotification(t('timers_list.error_loading_timers'), {
            type: 'error',
            autoHideDuration: 3000
          });
        }
      } catch (error) {
        console.error('Error fetching timers:', error);
        showNotification(t('timers_list.error_loading_timers'), {
          type: 'error',
          autoHideDuration: 3000
        });
      } finally {
        setLoading(false);
        initialRenderComplete.current = true;
      }
    };

    fetchTimers();
  }, [showNotification, t]);

  // Handle toggling timer enabled status
  const handleToggleEnabled = async (timer, isEnabled) => {
    if (updatingTimerIds.includes(timer.id)) return;
    
    setUpdatingTimerIds(prev => [...prev, timer.id]);
    setTimers(prev => 
      prev.map(t => t.id === timer.id ? { ...t, enabled: isEnabled } : t)
    );
    
    try {
      const payload = {
        id: timer.id,
        enabled: isEnabled,
      };
      
      const response = await Requester.patch(`scheduled-actions/update/${timer.id}/`, payload);
      
      if (!response.ok) {
        // Revert the optimistic update if API call fails
        setTimers(prev =>
          prev.map(t => t.id === timer.id ? { ...t, enabled: !isEnabled } : t)
        );
        
        console.error('Failed to update timer');
        showNotification(t('timers_list.timer_update_error'), {
          type: 'error',
          autoHideDuration: 3000
        });
      } else {
        showNotification(isEnabled ? t('timers_list.timer_activated') : t('timers_list.timer_deactivated'), {
          type: 'success',
          autoHideDuration: 3000
        });
      }
    } catch (error) {
      // Revert on error
      setTimers(prev =>
        prev.map(t => t.id === timer.id ? { ...t, enabled: !isEnabled } : t)
      );
      
      console.error('Error updating timer:', error);
      showNotification(t('timers_list.timer_update_error'), {
        type: 'error',
        autoHideDuration: 3000
      });
    } finally {
      setUpdatingTimerIds(prev => prev.filter(id => id !== timer.id));
    }
  };

  // Handle deleting a timer
  const handleDeleteTimer = async (timer) => {
    if (window.confirm(t('timers_list.confirm_delete'))) {
      setDeletingTimerIds(prev => [...prev, timer.id]);
      
      try {
        const response = await Requester.delete(`scheduled-actions/delete/${timer.id}/`);
        
        if (response.ok) {
          setTimers(prev => prev.filter(t => t.id !== timer.id));
          
          showNotification(t('timers_list.timer_deleted'), {
            type: 'success',
            autoHideDuration: 3000
          });
        } else {
          console.error('Failed to delete timer');
          showNotification(t('timers_list.delete_failed'), {
            type: 'error',
            autoHideDuration: 3000
          });
        }
      } catch (error) {
        console.error('Error deleting timer:', error);
        showNotification(t('timers_list.delete_error'), {
          type: 'error',
          autoHideDuration: 3000
        });
      } finally {
        setDeletingTimerIds(prev => prev.filter(id => id !== timer.id));
      }
    }
  };

  // Handle opening the actions menu
  const handleOpenActionsMenu = (event, timer) => {
    setAnchorEl(event.currentTarget);
    setSelectedTimer(timer);
  };

  // Handle closing the actions menu
  const handleCloseActionsMenu = () => {
    setAnchorEl(null);
    setSelectedTimer(null);
  };

  // Handle delete action from menu
  const handleDeleteFromMenu = () => {
    if (selectedTimer) {
      handleDeleteTimer(selectedTimer);
    }
    handleCloseActionsMenu();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t('timers_list.never');
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Handle direct delete button click
  const handleDirectDelete = (event, timer) => {
    event.stopPropagation(); // Prevent row click event
    handleDeleteTimer(timer);
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              to="/dashboard"
              component={RouterLink}
              sx={{
                '&:hover': { color: 'primary.main' }
              }}
            >
              {t('dashboard_navigation.home')}
            </Link>
            <Typography color="primary">{t('dashboard_navigation.timers')}</Typography>
          </Breadcrumbs>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TimerIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" fontWeight="500">
                {t('timers_list.timers_title')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              component={RouterLink}
              to="/dashboard/timers/create"
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {t('timers_list.create_new_timer')}
            </Button>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 3
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : timers.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1">{t('timers_list.no_timers')}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="timers table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('timers_list.enabled_column')}</TableCell>
                      <TableCell>{t('timers_list.name_column')}</TableCell>
                      <TableCell>{t('timers_list.interval_column')}</TableCell>
                      <TableCell>{t('timers_list.last_run_column')}</TableCell>
                      <TableCell>{t('timers_list.message_column')}</TableCell>
                      <TableCell width="60px" align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timers.map((timer) => (
                      <TableRow key={timer.id}>
                        <TableCell>
                          {updatingTimerIds.includes(timer.id) ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Switch 
                              checked={timer.enabled} 
                              onChange={(e) => handleToggleEnabled(timer, e.target.checked)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {timer.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{timer.interval}</TableCell>
                        <TableCell>{formatDate(timer.last_run)}</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: '200px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {timer.message}
                          </Typography>
                        </TableCell>
                        <TableCell width="60px" align="center">
                          <IconButton 
                            aria-label="actions"
                            onClick={(e) => handleOpenActionsMenu(e, timer)}
                            sx={{
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.light',
                              }
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseActionsMenu}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 150 }
          }}
        >
          <MenuItem 
            component={RouterLink}
            to={selectedTimer ? `/dashboard/timers/edit/${selectedTimer.id}` : '#'}
            onClick={handleCloseActionsMenu}
          >
            <ListItemText primary={t('timers_list.edit_timer')} />
          </MenuItem>
          <MenuItem 
            onClick={handleDeleteFromMenu}
            disabled={selectedTimer && deletingTimerIds.includes(selectedTimer.id)}
          >
            {selectedTimer && deletingTimerIds.includes(selectedTimer.id) ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null}
            <ListItemText 
              primary={t('common.delete')} 
              sx={selectedTimer && deletingTimerIds.includes(selectedTimer.id) ? 
                { color: 'text.disabled' } : { color: 'error.main' }
              }
            />
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
}